import { useTranslation } from 'react-i18next';

import { Button, Chip, Typography } from '@/components';
import { Card } from '@/components/ui/card';
import { InviteMemberDrawer, UserSchema, useGetCompanyUsersQuery } from '@/features/users';
import { useCollapsible } from '@/hooks';

function getInitials(user: UserSchema) {
  const initials = `${user.first_name?.charAt(0) ?? ''}${user.last_name?.charAt(0) ?? ''}`.trim();
  return (initials || user.email?.charAt(0) || '?').toUpperCase();
}

export function TeamCard() {
  const { t } = useTranslation(['team', 'settings']);
  const { isOpen, open, close } = useCollapsible();

  const { data: users = [] } = useGetCompanyUsersQuery();

  return (
    <Card className="p-6 gap-3">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
        <Typography as="h3" color="primary" size="title" weight="semibold">
          {t('Team')}
        </Typography>
        <Button size="sm" color="neutral" icon="plus" onClick={open}>
          {t('Add member')}
        </Button>
      </div>

      {users.length ? (
        <div>
          {users.map((user) => (
            <div
              key={user.id}
              className="flex items-center gap-3 border-t border-neutral-50 py-2.5 first:border-t-0"
            >
              <div className="bg-primary-100 text-primary-800 grid h-9 w-9 shrink-0 place-items-center rounded-full text-xs font-semibold">
                {getInitials(user)}
              </div>
              <div className="min-w-0 flex-1">
                <Typography as="p" size="body-3" color="primary" weight="semibold" className="truncate">
                  {`${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()}
                </Typography>
                <Typography as="p" size="body-3" color="secondary" className="truncate">
                  {user.email}
                </Typography>
              </div>
              <Chip color={user.status === 'active' ? 'success' : 'warning'}>{user.status}</Chip>
            </div>
          ))}
        </div>
      ) : (
        <Typography as="p" size="body-3" color="secondary">
          {t('No team members yet.')}
        </Typography>
      )}

      <InviteMemberDrawer isOpen={isOpen} onClose={close} />
    </Card>
  );
}
